/**
 * The PDF builder's state, requests and handlers, shared by `BuildPdfModal` and the desktop
 * project record's PDF builder: the report's photos and the chosen order, the job numbers, the
 * report text (saved as it's edited), making the PDF and downloading it.
 */
import {
  type AiResponse,
  AiResponseReport,
  AiResponseReportFile,
  type AiResponseReportFileWithUrl,
  BatchUpdateAiResponseReportFilesRequest,
} from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { queryKeys } from '../../../../hooks/react-query/constants';
import {
  useBatchGetAiResponseReportQuery,
  useBatchGetAiResponsesSystemQuery,
  useBatchUpdateAiResponseReportFilesMutation,
  useGetAiResponseReportDataQuery,
  useUpdateAiResponseReportMutation,
} from '../../../../hooks/react-query/useAiResponseReportQuery';
import { useEventBatchGetQuery } from '../../../../hooks/react-query/useEventsQuery';
import { useMemoQuery } from '../../../../hooks/react-query/useMemoQuery';
import { OpenAIClientService } from '../../../../tools/helpers';

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/i;
const DOWNLOAD_COOLDOWN_MS = 2000;

function sanitizeFilenameSegment(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return 'Unknown';
  return trimmed
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, '_')
    .slice(0, 120);
}

/** Local download name: Kalos_ProjectUpdate_<Job Number>_<Date of Download>.pdf */
function buildProjectUpdatePdfFilename(jobNumberPart: string, downloadDate: Date): string {
  const dateStr = format(downloadDate, 'yyyy-MM-dd');
  return `Kalos_ProjectUpdate_${sanitizeFilenameSegment(jobNumberPart)}_${dateStr}.pdf`;
}

export function getFilesToUpdate(
  files: AiResponseReportFileWithUrl[],
  selectedFileIds: number[],
  reportId: number,
): AiResponseReportFile[] {
  const selectedSet = new Set(selectedFileIds);
  const updates: AiResponseReportFile[] = [];

  for (const file of files) {
    if (!file.id) continue;
    const desiredSelected = selectedSet.has(file.id);
    const desiredOrder = desiredSelected ? selectedFileIds.indexOf(file.id) : 0;
    const currentSelected = file.isSelected ?? false;
    const currentOrder = file.displayOrder ?? 0;

    if (currentSelected !== desiredSelected || currentOrder !== desiredOrder) {
      updates.push(
        AiResponseReportFile.create({
          id: file.id,
          aiResponseReportId: reportId,
          isSelected: desiredSelected,
          displayOrder: desiredOrder,
          fieldMask: ['ai_response_report_id', 'is_selected', 'display_order'],
        }),
      );
    }
  }
  return updates;
}

function isImage(file: { fileName?: string; expiringUrl?: string }): boolean {
  const name = file.fileName ?? '';
  if (name) {
    return IMAGE_EXTENSIONS.test(name);
  }
  return !!file.expiringUrl;
}

export interface UseBuildPdfArgs {
  aiResponse: AiResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPdfBuilt?: () => void;
  /** The project's prime job, whose job number is preselected for the PDF. */
  primaryEventId?: number;
}

export const useBuildPdf = ({
  aiResponse,
  open,
  onOpenChange,
  onPdfBuilt,
  primaryEventId,
}: UseBuildPdfArgs) => {
  const hasPreselectedJobNumber = useRef(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  const [editedText, setEditedText] = useState('');
  const [reportId, setReportId] = useState<number | null>(null);
  const [textSaveStatus, setTextSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [blobPdfUrl, setBlobPdfUrl] = useState<string | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  /** True while fetching PDF bytes for preview/blob URL (after server returns pdfUrl) */
  const [isFetchingPdfBlob, setIsFetchingPdfBlob] = useState(false);
  const [pdfGenerationError, setPdfGenerationError] = useState<string | null>(null);
  const [isProcessingStep1, setIsProcessingStep1] = useState(false);
  const [reportTextHydratedForId, setReportTextHydratedForId] = useState<number | null>(null);
  const hasInitializedFromDbRef = useRef(false);
  const onPdfBuiltRef = useRef(onPdfBuilt);
  onPdfBuiltRef.current = onPdfBuilt;
  const lastDownloadTimeRef = useRef(0);
  const [isDownloadCooldown, setIsDownloadCooldown] = useState(false);
  const [selectedJobNumbers, setSelectedJobNumbers] = useState<string[]>([]);
  const [jobNumberFieldError, setJobNumberFieldError] = useState<string | null>(null);
  const jobNumbersInputRef = useRef<HTMLInputElement>(null);
  const jobNumbersErrorId = useId();

  const aiResponsesId = aiResponse.id ?? 0;

  const aiResponsesSystemQuery = useBatchGetAiResponsesSystemQuery({
    aiResponsesId,
    enabled: open && aiResponsesId > 0,
  });
  const memoIds = useMemo(() => {
    const results = aiResponsesSystemQuery.data?.results ?? [];
    return results.map((r) => r.memoId).filter((id) => id > 0);
  }, [aiResponsesSystemQuery.data?.results]);

  const memosQuery = useMemoQuery({
    filters: { idList: memoIds.join(',') },
    enabled: open && memoIds.length > 0,
  });

  const eventIds = useMemo(() => {
    const memos = memosQuery.data?.results ?? [];
    const ids = new Set<number>();
    for (const m of memos) {
      const eid = m.eventId ?? 0;
      if (eid > 0) ids.add(eid);
    }
    // The prime job too, so its job number can be preselected even if it has no notes here.
    if (primaryEventId && primaryEventId > 0) ids.add(primaryEventId);
    return Array.from(ids);
  }, [memosQuery.data?.results, primaryEventId]);

  const eventsQuery = useEventBatchGetQuery({
    filters: { idList: eventIds.join(',') },
    enabled: open && eventIds.length > 0,
  });

  const jobNumberOptions = useMemo(() => {
    const events = eventsQuery.data?.results ?? [];
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const e of events) {
      const jn = e.logJobNumber ?? '';
      if (jn && !seen.has(jn)) {
        seen.add(jn);
        options.push({ value: jn, label: jn });
      }
    }
    return options;
  }, [eventsQuery.data?.results]);

  // Preselect the prime job's number (or the only job's) once the options load, so most PDFs
  // need no picking. Removing it is still possible.
  useEffect(() => {
    if (hasPreselectedJobNumber.current || jobNumberOptions.length === 0) return;
    const primeJobNumber = eventsQuery.data?.results?.find(
      (event) => event.id === primaryEventId,
    )?.logJobNumber;
    const preselected =
      primeJobNumber || (jobNumberOptions.length === 1 ? jobNumberOptions[0].value : '');
    hasPreselectedJobNumber.current = true;
    if (preselected) setSelectedJobNumbers((current) => (current.length ? current : [preselected]));
  }, [jobNumberOptions, eventsQuery.data?.results, primaryEventId]);

  const getPdfDownloadFilename = useCallback(() => {
    const jobNumberRaw =
      selectedJobNumbers.length > 0
        ? selectedJobNumbers.join('-')
        : jobNumberOptions.length === 1
          ? jobNumberOptions[0].value
          : '';
    return buildProjectUpdatePdfFilename(jobNumberRaw || 'Unknown', new Date());
  }, [selectedJobNumbers, jobNumberOptions]);

  const isJobNumbersPipelineLoading = useMemo(() => {
    if (step !== 2) return false;
    if (
      aiResponsesId > 0 &&
      (aiResponsesSystemQuery.isLoading || aiResponsesSystemQuery.isFetching)
    ) {
      return true;
    }
    if (memoIds.length > 0 && (memosQuery.isLoading || memosQuery.isFetching)) {
      return true;
    }
    if (eventIds.length > 0 && (eventsQuery.isLoading || eventsQuery.isFetching)) {
      return true;
    }
    return false;
  }, [
    step,
    aiResponsesId,
    memoIds.length,
    eventIds.length,
    aiResponsesSystemQuery.isLoading,
    aiResponsesSystemQuery.isFetching,
    memosQuery.isLoading,
    memosQuery.isFetching,
    eventsQuery.isLoading,
    eventsQuery.isFetching,
  ]);

  const queryClient = useQueryClient();

  const batchGetQuery = useBatchGetAiResponseReportQuery({
    aiResponsesId,
    enabled: open && aiResponsesId > 0,
  });

  const existingReportId = useMemo(() => {
    const results = batchGetQuery.data?.results;
    if (!results || results.length === 0) return null;
    return results[0]?.id ?? null;
  }, [batchGetQuery.data?.results]);

  const effectiveReportId = reportId ?? existingReportId;

  const reportDataQuery = useGetAiResponseReportDataQuery({
    reportId: effectiveReportId ?? 0,
    enabled: open && (effectiveReportId ?? 0) > 0,
  });

  const updateReportMutation = useUpdateAiResponseReportMutation();
  const batchUpdateReportFilesMutation = useBatchUpdateAiResponseReportFilesMutation();

  const files = useMemo(() => {
    const list = reportDataQuery.data?.files ?? [];
    const imageFiles = list.filter((f) => isImage(f));
    return imageFiles.sort((a, b) => {
      const aSel = a.isSelected ?? false;
      const bSel = b.isSelected ?? false;
      if (aSel !== bSel) return aSel ? -1 : 1;
      if (aSel && bSel) return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      return (a.id ?? 0) - (b.id ?? 0);
    });
  }, [reportDataQuery.data?.files]);

  const serverReportText = useMemo(() => {
    if (
      !effectiveReportId ||
      effectiveReportId <= 0 ||
      reportTextHydratedForId !== effectiveReportId
    ) {
      return null;
    }
    return reportDataQuery.data?.report?.text ?? '';
  }, [effectiveReportId, reportTextHydratedForId, reportDataQuery.data?.report?.text]);

  const isStep2ReportTextReady =
    !!effectiveReportId && effectiveReportId > 0 && reportTextHydratedForId === effectiveReportId;

  useEffect(() => {
    hasInitializedFromDbRef.current = false;
    setReportTextHydratedForId(null);
  }, [effectiveReportId]);

  useEffect(() => {
    if (!effectiveReportId || effectiveReportId <= 0) return;
    if (!reportDataQuery.isSuccess || !reportDataQuery.data?.report) return;
    if (reportTextHydratedForId === effectiveReportId) return;

    const text = reportDataQuery.data.report.text ?? '';
    setEditedText(text);
    setReportTextHydratedForId(effectiveReportId);
  }, [
    effectiveReportId,
    reportDataQuery.isSuccess,
    reportDataQuery.data?.report,
    reportTextHydratedForId,
  ]);

  useEffect(() => {
    if (!reportDataQuery.data?.files || hasInitializedFromDbRef.current) return;
    const imageFiles = reportDataQuery.data.files.filter((f) => isImage(f));
    const selected = imageFiles
      .filter((f) => f.isSelected)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map((f) => f.id)
      .filter((id): id is number => id !== undefined && id > 0);
    if (selected.length > 0) {
      setSelectedFileIds(selected);
    }
    hasInitializedFromDbRef.current = true;
  }, [reportDataQuery.data?.files]);

  const debouncedSaveText = useMemo(
    () =>
      debounce(async (text: string, rid: number) => {
        setTextSaveStatus('saving');
        try {
          await updateReportMutation.mutateAsync(
            AiResponseReport.create({ id: rid, text, fieldMask: ['text'] }),
          );
          setTextSaveStatus('saved');
        } catch {
          setTextSaveStatus('idle');
          toast({
            title: 'Save failed',
            description: 'Could not save report text. Please try again.',
            variant: 'destructive',
          });
        }
      }, 1000),
    [updateReportMutation],
  );

  useEffect(() => {
    if (
      serverReportText === null ||
      editedText === serverReportText ||
      !effectiveReportId ||
      effectiveReportId <= 0 ||
      step !== 2
    ) {
      return () => debouncedSaveText.cancel();
    }
    debouncedSaveText(editedText, effectiveReportId);
    return () => debouncedSaveText.cancel();
  }, [editedText, serverReportText, effectiveReportId, step, debouncedSaveText]);

  useEffect(() => {
    if (!pdfUrl) {
      setBlobPdfUrl(null);
      setIsFetchingPdfBlob(false);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setIsFetchingPdfBlob(true);
    fetch(pdfUrl)
      .then((res) => res.arrayBuffer())
      .then((data) => {
        if (cancelled) return;
        const blob = new Blob([data], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(blob);
        setBlobPdfUrl(objectUrl);
        setIsFetchingPdfBlob(false);
        onPdfBuiltRef.current?.();
      })
      .catch(() => {
        if (!cancelled) {
          setIsFetchingPdfBlob(false);
          toast({
            title: 'Preview failed',
            description: 'Could not load PDF preview. You can still download the PDF.',
            variant: 'destructive',
          });
          onPdfBuiltRef.current?.();
        }
      });
    return () => {
      cancelled = true;
      setIsFetchingPdfBlob(false);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pdfUrl]);

  const ensureReportExists = async (): Promise<number> => {
    if (effectiveReportId && effectiveReportId > 0) return effectiveReportId;
    const created = await OpenAIClientService.CreateAiResponseReport?.(
      AiResponseReport.create({
        text: aiResponse.response ?? '',
        aiResponsesId,
      }),
    );
    if (!created?.id) throw new Error('Failed to create report');
    setReportId(created.id);
    queryClient.invalidateQueries({
      queryKey: [queryKeys.aiResponseReport.root, 'batch', aiResponsesId],
    });
    return created.id;
  };

  const handleStep1Next = async () => {
    setIsProcessingStep1(true);
    try {
      let rid = effectiveReportId ?? null;
      if (!rid) {
        rid = await ensureReportExists();
      }
      if (rid && files.length > 0) {
        const updates = getFilesToUpdate(files, selectedFileIds, rid);
        if (updates.length > 0) {
          await batchUpdateReportFilesMutation.mutateAsync(
            BatchUpdateAiResponseReportFilesRequest.create({ updates }),
          );
        }
      }
      setStep(2);
    } catch {
      toast({
        title: 'Could not proceed',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessingStep1(false);
    }
  };

  const handleToggleFile = (file: AiResponseReportFileWithUrl) => {
    const id = file.id;
    if (!id) return;
    setSelectedFileIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((f) => f !== id);
      }
      return [...prev, id];
    });
  };

  const handleDeselectAll = () => {
    setSelectedFileIds([]);
  };

  /** Saves the report text to the PDF record `rid` and makes the PDF (step 2's Next, after checks). */
  const generatePdfForReport = async (rid: number, text: string = editedText) => {
    setPdfGenerationError(null);
    setIsDownloadCooldown(false);
    setBlobPdfUrl(null);
    setPdfUrl(null);
    setStep(3);
    setIsGeneratingPdf(true);

    try {
      await updateReportMutation.mutateAsync(
        AiResponseReport.create({ id: rid, text, fieldMask: ['text'] }),
      );

      const urlObj = await OpenAIClientService.DownloadAiResponseReport?.(
        AiResponseReport.create({
          id: rid,
          jobNumbers: selectedJobNumbers.length > 0 ? selectedJobNumbers.join(', ') : '',
        }),
      );
      if (urlObj?.url) {
        setPdfUrl(urlObj.url);
        setPdfGenerationError(null);
      } else {
        throw new Error('No URL returned');
      }
    } catch {
      setPdfGenerationError('Could not generate PDF. Please try again.');
      toast({
        title: 'PDF generation failed',
        description: 'Could not generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePersistAndGoToStep3 = async () => {
    if (jobNumberOptions.length > 0 && selectedJobNumbers.length === 0) {
      setJobNumberFieldError('Please select at least one job number.');
      requestAnimationFrame(() => {
        jobNumbersInputRef.current?.focus();
      });
      return;
    }

    let rid = effectiveReportId ?? null;
    if (!rid) {
      try {
        rid = await ensureReportExists();
      } catch {
        toast({
          title: 'No report',
          description: 'Report is not available. Please try again.',
          variant: 'destructive',
        });
        return;
      }
    }

    await generatePdfForReport(rid);
  };

  const handleDownloadPdf = async () => {
    if (!effectiveReportId) return;
    const filename = getPdfDownloadFilename();
    try {
      const urlObj = await OpenAIClientService.DownloadAiResponseReport?.(
        AiResponseReport.create({
          id: effectiveReportId,
          jobNumbers: selectedJobNumbers.length > 0 ? selectedJobNumbers.join(', ') : '',
        }),
      );
      if (urlObj?.url) {
        const a = document.createElement('a');
        a.href = urlObj.url;
        a.download = filename;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.click();
      }
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download PDF. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setStep(1);
    setSelectedFileIds([]);
    setSelectedJobNumbers([]);
    hasPreselectedJobNumber.current = false;
    setJobNumberFieldError(null);
    hasInitializedFromDbRef.current = false;
    setReportTextHydratedForId(null);
    setEditedText('');
    setPdfUrl(null);
    setBlobPdfUrl(null);
    setReportId(null);
    setTextSaveStatus('idle');
    setIsGeneratingPdf(false);
    setIsFetchingPdfBlob(false);
    setPdfGenerationError(null);
    setIsProcessingStep1(false);
    lastDownloadTimeRef.current = 0;
    setIsDownloadCooldown(false);
  };

  useEffect(() => {
    if (!isDownloadCooldown) return;
    const id = setTimeout(() => setIsDownloadCooldown(false), DOWNLOAD_COOLDOWN_MS);
    return () => clearTimeout(id);
  }, [isDownloadCooldown]);

  useEffect(() => {
    if (selectedJobNumbers.length > 0) {
      setJobNumberFieldError(null);
    }
  }, [selectedJobNumbers.length]);

  useEffect(() => {
    if (step !== 2) {
      setJobNumberFieldError(null);
    }
  }, [step]);
  const isLoading =
    batchGetQuery.isLoading ||
    (reportDataQuery.isLoading && !!effectiveReportId) ||
    reportDataQuery.isFetching;

  const selectedCount = selectedFileIds.length;

  /** Server PDF generation or client blob load — download stays disabled until both finish */
  const isPdfPipelineBusy = isGeneratingPdf || isFetchingPdfBlob;

  /** After the PDF is saved: Download PDF reads Downloaded for a moment. */
  const markPdfDownloaded = () => {
    lastDownloadTimeRef.current = Date.now();
    setIsDownloadCooldown(true);
  };

  return {
    markPdfDownloaded,
    hasPreselectedJobNumber,
    step,
    setStep,
    selectedFileIds,
    setSelectedFileIds,
    editedText,
    setEditedText,
    reportId,
    textSaveStatus,
    pdfUrl,
    blobPdfUrl,
    isGeneratingPdf,
    isFetchingPdfBlob,
    pdfGenerationError,
    setPdfGenerationError,
    isProcessingStep1,
    lastDownloadTimeRef,
    isDownloadCooldown,
    setIsDownloadCooldown,
    selectedJobNumbers,
    setSelectedJobNumbers,
    jobNumberFieldError,
    jobNumbersInputRef,
    jobNumbersErrorId,
    aiResponsesId,
    memoIds,
    eventIds,
    jobNumberOptions,
    getPdfDownloadFilename,
    isJobNumbersPipelineLoading,
    batchGetQuery,
    existingReportId,
    effectiveReportId,
    reportDataQuery,
    batchUpdateReportFilesMutation,
    files,
    isStep2ReportTextReady,
    ensureReportExists,
    handleStep1Next,
    handleToggleFile,
    handleDeselectAll,
    handlePersistAndGoToStep3,
    generatePdfForReport,
    handleDownloadPdf,
    handleClose,
    isLoading,
    selectedCount,
    isPdfPipelineBusy,
  };
};
